import {E, Position, wsClient} from '../imports';


/**
 * PositionsManager is a singleton that manages the user's trading positions.
 * It handles WebSocket messages for position updates (GET_POSITIONS, UPDATE_POSITION, REMOVE_POSITION).
 * Positions are keyed by symbol in the Map.
 */
class PositionsManager {
    positions: Map<string, Position> = new Map();

    isConnected: boolean = false;
    portfolioTotalValue: number = 0; // Cached for percentage calculations

    // Callback fired when positions update
    onPositionsUpdated: ((positions: Map<string, Position>) => void) | null = null;


    /**
     * Initialize positions by requesting current data from server.
     * Should be called after WebSocket connection is established.
     */
    init = async (): Promise<void> => {
        // Register callbacks with WsClient
        wsClient.onPositionUpdate = this.handlePositionUpdate;
        wsClient.onPositionRemove = this.handlePositionRemove;

        try {
            const positionsArray = await wsClient.sendRequest(
                E.WsRoute.POSITIONS,
                E.WsPositionsCommand.GET_ALL,
                undefined,
                10
            );

            // Convert array to Map keyed by symbol (position.id)
            this.positions = new Map();
            if (Array.isArray(positionsArray)) {
                positionsArray.forEach((posData: any) => {
                    // Create Position instance from plain object
                    const position = this.createPositionFromData(posData);
                    this.positions.set(position.id, position);
                });
            }

            this.isConnected = true;
            this.onPositionsUpdated?.(this.positions);
            
            console.log(`[PositionsManager] Loaded ${this.positions.size} positions`);
        } catch (error) {
            console.error('[PositionsManager] Failed to load positions:', error);
            throw error;
        }
    };

    /**
     * Disconnect and clear positions data.
     */
    disconnect = () => {
        this.positions.clear();
        this.isConnected = false;
        this.onPositionsUpdated?.(this.positions);
        console.log('[PositionsManager] Disconnected and cleared positions');
    };

    /**
     * Handle incoming WebSocket push message for position update.
     * Payload can be full position data (first update) or delta (subsequent updates).
     * Position ID is the symbol.
     */
    handlePositionUpdate = (payload: Partial<Position> & { id: string }) => {
        const { id } = payload;
        const existingPosition = this.positions.get(id);

        if (!existingPosition) {
            // New position - create from full data
            const newPosition = this.createPositionFromData(payload);
            this.positions.set(id, newPosition);
            console.log(`[PositionsManager] New position added: ${id}`);
        } else {
            // Delta update - merge changed fields into existing position
            this.mergePositionDelta(existingPosition, payload);
            console.log(`[PositionsManager] Position updated: ${id}`);
        }

        this.onPositionsUpdated?.(this.positions);
    };

    /**
     * Handle incoming WebSocket push message for position removal.
     */
    handlePositionRemove = (payload: { id: string }) => {
        const { id } = payload;
        const removed = this.positions.delete(id);
        
        if (removed) {
            console.log(`[PositionsManager] Position removed: ${id}`);
            this.onPositionsUpdated?.(this.positions);
        } else {
            console.warn(`[PositionsManager] Attempted to remove non-existent position: ${id}`);
        }
    };

    /**
     * Create a Position instance from plain object data.
     */
    private createPositionFromData(data: any): Position {
        return new Position(
            data.id,
            data.status,
            data.instrument,
            data.contractsHeld,
            data.contractsAvailable,
            data.orderIds || [],
            data.openedAt,
            data.closedAt,
            data.lastPrice,
            data.stats,
            data.detached || false
        );
    }

    /**
     * Merge delta update into existing position.
     * Only updates fields that are present in the delta.
     */
    private mergePositionDelta(position: Position, delta: Partial<Position>) {
        // Update primitive fields
        if (delta.status !== undefined) position.status = delta.status;
        if (delta.contractsHeld !== undefined) position.contractsHeld = delta.contractsHeld;
        if (delta.contractsAvailable !== undefined) position.contractsAvailable = delta.contractsAvailable;
        if (delta.lastPrice !== undefined) position.lastPrice = delta.lastPrice;
        if (delta.openedAt !== undefined) position.openedAt = delta.openedAt;
        if (delta.closedAt !== undefined) position.closedAt = delta.closedAt;
        if (delta.detached !== undefined) position.detached = delta.detached;

        // Merge stats object (delta may only contain changed stat fields)
        if (delta.stats && position.stats) {
            position.stats = { ...position.stats, ...delta.stats };
        } else if (delta.stats) {
            position.stats = delta.stats;
        }

        // Update orderIds array if provided
        if (delta.orderIds !== undefined) {
            position.orderIds = delta.orderIds;
        }

        // Update instrument if provided (unlikely but handle it)
        if (delta.option) {
            position.option = delta.option;
        }
    }
}

export const positionsManager = new PositionsManager();
